import { ArrowUpRightIcon, PlayIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { instagramHandle } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnSecondaryLg } from '../../ui/styles';

/**
 * Het slot van het journal, op blush (de footer is klei, dus hier geen klei): de podcast met
 * onderwerpen en afleveringen, elk met een eigen link (leeg = de link naar de show). Daaronder
 * één rustige regel naar Instagram met de handle uit Instellingen; geen feed (snelheid, privacy).
 */
export function JournalPodcast() {
  const pages = usePages();
  // De handle uit de actuele instellingen (Instellingen › Salon & contact), net als de footer.
  const {
    salon
  } = useSite();
  const copy = pages.journal;
  const showUrl = copy.podcast.url;
  const handle = salon.instagram ? instagramHandle(salon.instagram, salon.name) : '';
  return <Section tone="blush" labelledBy="podcast-title" contained={false} data-id="element-7886" data-mp="1yid6ryxc0m5">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7887" data-mp="3u2svmzvmk79o">
        <div className="lg:col-span-5" data-id="element-7888" data-mp="3p3q2acp0bphj">
          {copy.podcast.eyebrow && <p className="text-[14px] text-ink" data-id="element-7889" data-mp="3p37zsybr4f16">{fill(copy.podcast.eyebrow)}</p>}
          <h2 id="podcast-title" className={`mt-5 ${sectionHeading} text-ink`} data-id="element-7890" data-mp="3k5oghk075c2d">
            <HeadingText heading={copy.podcast.heading} data-id="element-7891" data-mp="3fdod1hkx4111" />
          </h2>
          {copy.podcast.body && <p className="mt-7 max-w-md text-[17px] leading-relaxed text-ink" data-id="element-7892" data-mp="3aebsj3hir61m">{fill(copy.podcast.body)}</p>}
          {copy.podcast.topics.length > 0 && <ul className="mt-7 flex flex-wrap gap-2" aria-label="Onderwerpen" data-id="element-7893" data-mp="36b5c530ozyhf">
              {copy.podcast.topics.map(t => <li key={t} className="rounded-full border border-ink/20 px-3 py-1 text-[13px] text-ink" data-id="element-7894" data-mp="2wfc33tm6mn4e">
                  {t}
                </li>)}
            </ul>}
          {copy.podcast.cta && showUrl && <a href={showUrl} target="_blank" rel="noreferrer" className={`mt-9 ${btnSecondaryLg}`} data-id="element-7895" data-mp="2rrazezcxfi7m">
              {fill(copy.podcast.cta)}
              <span className="sr-only" data-id="element-7896" data-mp="2cvn09omgtlal"> {shopLabels.newTab}</span>
              <ArrowUpRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7897" data-mp="2hsvi1o3pbnbb" />
            </a>}
        </div>

        <div className="lg:col-span-7" data-id="element-7898" data-mp="35kaej08sraen">
          {copy.episodes.length > 0 && <ul className="divide-y divide-ink/15 border-y border-ink/15" aria-label="Afleveringen" data-id="element-7899" data-mp="3ag9b3b4a3yq5">
              {copy.episodes.map(e => {
            const href = e.url || showUrl;
            return <li key={e.number} data-id="element-7900" data-mp="30ov4ykhto0o1">
                    <a href={href} target="_blank" rel="noreferrer" className="group grid w-full gap-3 py-7 text-left sm:grid-cols-12 sm:items-center sm:gap-6" data-id="element-7901" data-mp="3ogk2cf9i3fbu">
                      <span className="flex items-center gap-4 sm:col-span-5" data-id="element-7902" data-mp="1hsjocmt3worh">
                        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ink/25 text-ink transition-colors duration-150 ease-soft group-hover:border-ink group-hover:bg-ink group-hover:text-shell" data-id="element-7903" data-mp="1hqgnt8ubvd8i">
                          <PlayIcon className="h-3 w-3 fill-current" strokeWidth={1.5} data-id="element-7904" data-mp="yt5q3socgrv6" />
                        </span>
                        <span data-id="element-7905" data-mp="tvmaopfwu91n">
                          <span className="sr-only" data-id="element-7906" data-mp="twfd5e7u6cdz">Beluister </span>
                          <span className="block type-title text-ink" data-id="element-7907" data-mp="17vf1slvcmra5">{e.title}</span>
                          <span className="mt-0.5 block text-[13px] text-muted" data-id="element-7908" data-mp="13sil50pdd58u">
                            Afl. {e.number} · {e.duration}
                            {e.date ? ` · ${e.date}` : ''}
                          </span>
                        </span>
                      </span>
                      <span className="text-[15px] leading-relaxed text-muted sm:col-span-7" data-id="element-7909" data-mp="3ailu4zihlvb4">{e.summary}</span>
                      <span className="sr-only" data-id="element-7910" data-mp="3eixiama26drc">{shopLabels.newTab}</span>
                    </a>
                  </li>;
          })}
            </ul>}

          {handle && copy.podcast.instagramLine && <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px] text-muted" data-id="element-7911" data-mp="3oj6fo3fse58b">
              {fill(copy.podcast.instagramLine)}
              <a href={salon.instagram} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7912" data-mp="1cnjoa0yoshl">
                @{handle}
                <span className="sr-only" data-id="element-7913" data-mp="64fewxv2bme2"> op Instagram {shopLabels.newTab}</span>
                <ArrowUpRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7914" data-mp="b1xehxn6wp9e" />
              </a>
            </p>}
        </div>
      </div>
    </Section>;
}